import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { map, switchMap } from 'rxjs';
import { PICFLOW_STORYBLOK } from '../../picflow-storyblok';
import { RedirectService } from '../../services/redirect.service';

/**
 * 301s the legacy converter URLs to the /tools/convert/* equivalents:
 *   /convert/:format        -> /tools/convert/:format
 *   /:lang/convert/:format  -> /:lang/tools/convert/:format
 * Legacy routes must stay registered indefinitely — the /convert/* cluster
 * holds the site's largest external backlink pool.
 */
export const convertLegacyRedirectGuard: CanActivateFn = route => {
  const redirectService = inject(RedirectService);

  const lang = route.params.lang;
  const format = route.params.format;
  const path = lang ? [lang, 'tools', 'convert', format] : ['tools', 'convert', format];

  return redirectService.redirectTo(path, {
    redirectType: 'permanent',
    queryParams: route.queryParams,
  });
};

/**
 * /tools/convert without a format has no page of its own — 301 to the
 * image converter hub story (localized slug, e.g. /de/bildkonvertierer)
 * instead of falling through to a 404.
 */
export const convertIndexRedirectGuard: CanActivateFn = route => {
  const redirectService = inject(RedirectService);
  const storyblokService = inject(PICFLOW_STORYBLOK);

  const lang = route.params.lang;

  return storyblokService.getStory('tools/image-converter', route).pipe(
    map(resp => {
      const story = resp && resp.story;
      const translated = story?.translated_slugs?.find(ts => ts.lang === lang);
      return (translated?.path || story?.full_slug || 'tools/image-converter').replace(/\/$/, '');
    }),
    switchMap(slug => {
      const path = lang ? [lang, slug] : [slug];
      return redirectService.redirectTo(path, {
        redirectType: 'permanent',
        queryParams: route.queryParams,
      });
    })
  );
};
